import { throwError as observableThrowError, Observable } from "rxjs";
import { Injectable } from "@angular/core";
import {
  HttpEvent,
  HttpInterceptor,
  HttpHandler,
  HttpRequest,
  HttpErrorResponse,
} from "@angular/common/http";
import { Router } from "@angular/router";
import { ToastrService } from "./toastr.service";
import { TENANT_KEY, LOCALE_KEY, TIMEZONE_KEY, X_ORIGIN, X_ORIGIN_VALUE } from "app/const";
import { AuthService } from "../shared/auth/auth.service";
import { exibirImagemLoading } from "../../assets/js/barload";
import { catchError } from "rxjs/operators";
import { TranslateService } from "@ngx-translate/core";
import { environment } from "environments/environment";
import { AbTenant } from "app/abstract/full-pages/admin/tenant.abstract";
import { OperationValidationService } from "./operation-validation.service";
import { DesignSystemSigmaService } from "./design-system-sigma.service";
import { AbPostHog } from "app/abstract/services/posthog.abstract";
import { PostHogEventEnum } from "enums/posthog-event.enum";

@Injectable({ providedIn: "root" })
export class AuthInterceptor implements HttpInterceptor {
  private url;

  constructor(
    private toastService: ToastrService,
    private router: Router,
    private service: AuthService,
    private tenantService: AbTenant,
    private translate: TranslateService,
    private operationValidation: OperationValidationService,
    private dssService: DesignSystemSigmaService,
    private posthog: AbPostHog
  ) { }

  intercept(
    req: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    if (this.router.url !== this.url) {
      this.url = this.router.url;
      this.toastService.clear();
    }

    let headers = req.headers;

    const token = this.service.getAccessToken();
    if (token) {
      const isRefreshToken =
        req.body &&
        req.body.toString().indexOf("&grant_type=refresh_token") > 0;
      if (!isRefreshToken && !headers.get("Authorization")) {
        headers = headers.set("Authorization", "Bearer " + token);
      }
    }

    const tenantSchema = this.tenantService.getTenantSchemaStorage();
    const tenant = this.tenantService.getTenantStorage();
    // Prioriza o tenant recém-selecionado: um tenant_schema vazado de tela de log
    // (não limpo por cancelamento/navegação) não deve sobrepor a conta ativa.
    const schemaParaEnviar = tenant?.schema || tenantSchema;
    if (schemaParaEnviar) {
      headers = headers.set(TENANT_KEY, schemaParaEnviar);
    }

    headers = headers.set(
      LOCALE_KEY,
      this.service.getLocale() ||
      this.translate.currentLang ||
      this.translate.getDefaultLang() ||
      "pt_BR"
    );

    const stringtime = this.service.getTimeZone();

    if (stringtime) headers = headers.set(TIMEZONE_KEY, stringtime);

    const isMessageOn = this.toastService.isOn();

    if (this.isCorrespondenciaIntPage())
      headers = headers.set(X_ORIGIN, X_ORIGIN_VALUE);

    const requestWithHeaders = req.clone({ headers });

    return next.handle(requestWithHeaders).pipe(
      catchError((error) => {
        if (error instanceof HttpErrorResponse && this.isHttpStatusErro(error.status) && error.status !== 428) {

          this.posthog.capture(PostHogEventEnum.API_ERRO, {
            status_code: error.status,
            endpoint: this.extrairPathSemQuery(error.url ?? req.url),
            metodo_http: req.method,
            ...this.extrairDetalheErro(error),
            ...(this.isAutenticacao(error.status) ? { categoria: 'autenticacao' } : {})
          });
        }

        if (error instanceof HttpErrorResponse) {
          if (error.status === 428) {
            return this.operationValidation.handlePrecondition(requestWithHeaders, next, error);
          }

          if (this.isUsuarioExcluido(error)) {
            this.service.logout();
            this.router.navigate(["/pages/login"]);
            return observableThrowError(error);
          }

          if (isMessageOn) {
            return this.handlerError(error);
          }
        }

        return observableThrowError(error);
      })
    );
  }

  private isHttpStatusErro(status: number): boolean {
    return status >= 400 && status < 600;
  }

  private isAutenticacao(status: number): boolean {
    return status === 401 || status === 403;
  }

  private extrairPathSemQuery(url: string | null | undefined): string {
    if (!url) return '';
    return url.split('#')[0].split('?')[0];
  }

  private extrairDetalheErro(error: HttpErrorResponse): Record<string, unknown> {
    const detalhe: Record<string, unknown> = {};
    if (error.statusText) {
      detalhe.status_text = error.statusText;
    }
    try {
      const corpo = error.error;
      if (corpo && typeof corpo === 'object') {
        if (corpo.error !== undefined) {
          detalhe.erro_codigo =
            typeof corpo.error === 'string' ? corpo.error : JSON.stringify(corpo.error);
        }
        if (corpo.error_description) {
          detalhe.erro_descricao = corpo.error_description;
        }
        if (corpo.message) {
          detalhe.erro_mensagem = corpo.message;
        }
        if (corpo.path) {
          detalhe.erro_path = corpo.path;
        }
      } else if (typeof corpo === 'string' && corpo.length > 0) {
        detalhe.erro_corpo = corpo.slice(0, 1000);
      }
    } catch {
      void 0;
    }
    return detalhe;
  }

  private handlerError(error: HttpErrorResponse): Observable<any> {
    if (error.status === 409 || error.status === 499) return observableThrowError(error);
    if (error.url?.includes('contas/pre-cadastro/validate')) return observableThrowError(error);
    if (error.url?.includes('auth/center/v1/has-contract')) return observableThrowError(error);
    if (this.isBootstrapDss(error)) return observableThrowError(error);
    // Capabilities (feature flags) degradam graciosamente no FeatureFlagService; uma
    // falha aqui (ex.: sem tenant no bootstrap da folha em select-conta) não deve
    // deslogar nem exibir toast.
    if (error.url?.includes('capabilities')) return observableThrowError(error);
    this.toastService.error(error);
    exibirImagemLoading(false, this.service.carregaBarload);

    if (
      (error &&
        error.url &&
        error.url.includes(environment.urlApi) &&
        (error.status === 401 || error.status === 403)) ||
      (error.error && error.error.missingTenant)
    ) {
      if (this.isDssLayout()) {
        this.router.navigate(["pages/error/dss"]);
        return
      }

      if (this.redirectToLogin(error)) {
        this.router.navigate(["/pages/login"]);
      } else {
        if (!this.ignorarRedirect()) {
          setTimeout(() => {
            this.router.navigate([""]);
          }, 100);
        }
      }
    }

    return observableThrowError(error);
  }

  // includes() casaria com paths vizinhos, como admin/tenants/dss_layout_backup ou
  // admin/tenants/dss_layout/extra, suprimindo o redirect de endpoint que não é bootstrap.
  // Compara o path sem query com o fim exato do endpoint: elimina o falso positivo sem
  // depender da formatação de environment.urlApi, que a igualdade de URL inteira exigiria.
  private isBootstrapDss(error: HttpErrorResponse): boolean {
    if (!this.isDssLayout()) return false;
    return this.extrairPathSemQuery(error.url).endsWith("admin/tenants/dss_layout");
  }

  private ignorarRedirect(): boolean {
    return (
      this.router.url.indexOf("list-cameras") > -1 ||
      this.router.url.indexOf("pages/softphone") > -1 ||
      this.isCorrespondenciaIntPage()
    );
  }

  private isCorrespondenciaIntPage(): boolean {
    return this.router.url.indexOf("pages/list-correspondencias-int") > -1;
  }

  private redirectToLogin(error): boolean {
    return (
      error.url.indexOf("auth/web/login") > 0 ||
      error.url.indexOf("auth/web/validar-token-mfa") > 0 ||
      error.url.indexOf("auth/web/v2/login") > 0 ||
      error.url.indexOf("auth/web/v2/validar-token-mfa") > 0 ||
      error.url.indexOf("auth/web/refresh-session") > 0 ||
      error.error.error === "invalid_token" ||
      this.service.getAccessToken() == null ||
      error.error.missingTenant
    );
  }

  private isUsuarioExcluido(error: HttpErrorResponse): boolean {
    if (error.status !== 406 || !error.url?.includes(environment.urlApi)) {
      return false;
    }
    const corpo = error.error;
    return corpo && typeof corpo === 'object' && corpo.message === 'Usuário não encontrado';
  }

  isDssLayout(): boolean {
    return this.dssService.isDssLayout();
  }
}
